import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useRoleStore, useBranchStore } from '@/lib/store';
import { toast } from 'sonner';
import type { SusuClient, SusuAuditLog, DashboardStats, SusuLoan, User, DailySession, SystemSettings, SusuTransaction, Branch, ProfitLossData, CashFlowData, ClientDocument } from '@shared/types';
export async function rbacApi<T>(path: string, init?: RequestInit): Promise<T> {
  const role = useRoleStore.getState().role;
  const clientId = useRoleStore.getState().userId;
  const userBranchId = useRoleStore.getState().branchId;
  const selectedBranchId = useBranchStore.getState().selectedBranchId;
  // Privileged roles can view 'Global' if 'all' is selected
  const activeBranchId = (role === 'Admin' || role === 'Accountant' || role === 'Manager')
    ? (selectedBranchId === 'all' ? 'Global' : selectedBranchId)
    : userBranchId;
  const headers: Record<string, string> = {
    ...init?.headers as Record<string, string>,
    'x-user-role': role,
    'x-user-branch-id': activeBranchId
  };
  if (role === 'Client' && clientId) {
    headers['x-client-id'] = clientId;
  }
  return api<T>(path, { ...init, headers });
}
export function useClientSelf() {
  const role = useRoleStore(s => s.role);
  return useQuery({
    queryKey: ['client-self'],
    queryFn: () => rbacApi<SusuClient>('/api/client/self'),
    enabled: role === 'Client',
    retry: 1,
  });
}
export function useClientSelfTransactions() {
  return useQuery({
    queryKey: ['client-self-transactions'],
    queryFn: () => rbacApi<{ items: SusuTransaction[]; next: string | null }>('/api/client/transactions')
  });
}
export function useCurrentUser() {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: () => rbacApi<User>('/api/me'),
  });
}
export function useUpdateCurrentUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<User>) => rbacApi<User>('/api/me', { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}
export function useEndDaySession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => rbacApi<DailySession>('/api/sessions/reconcile', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] }),
  });
}
export function useBranches(enabled = true) {
  return useQuery({
    queryKey: ['branches'],
    queryFn: () => rbacApi<Branch[]>('/api/branches'),
    enabled,
  });
}
export function useCreateBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Branch>) => rbacApi<Branch>('/api/branches', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['branches'] }),
  });
}
export function useUpdateBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Branch> }) => rbacApi<Branch>(`/api/branches/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['branches'] }),
  });
}
export function useSettings(enabled = true) {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => rbacApi<SystemSettings>('/api/settings'),
    enabled,
  });
}
export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SystemSettings>) => rbacApi<SystemSettings>('/api/settings', { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });
}
export function useClients(filters?: { atRisk?: boolean; territory?: string }) {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  if (filters?.atRisk) queryParams.append('atRisk', 'true');
  if (filters?.territory) queryParams.append('territory', filters.territory);
  queryParams.append('branchId', selectedBranchId);
  return useQuery({
    queryKey: ['clients', filters, selectedBranchId],
    queryFn: () => rbacApi<{ items: SusuClient[]; next: string | null }>(`/api/clients?${queryParams.toString()}`)
  });
}
export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; phone: string; territory?: string; branchId?: string }) =>
      rbacApi<SusuClient>('/api/clients', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}
export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SusuClient> }) =>
      rbacApi<SusuClient>(`/api/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      rbacApi(`/api/clients/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useClientTransactions(clientId: string) {
  return useQuery({
    queryKey: ['client-transactions', clientId],
    queryFn: () => rbacApi<{ items: SusuTransaction[]; next: string | null }>(`/api/clients/${clientId}/transactions`),
    enabled: !!clientId,
  });
}
export function useDashboardStats() {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  return useQuery({
    queryKey: ['dashboard-stats', selectedBranchId],
    queryFn: () => rbacApi<DashboardStats>(`/api/dashboard/stats?${queryParams.toString()}`),
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: 1000,
    staleTime: 30000,
  });
}
export function useAuditLogs() {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  return useQuery({
    queryKey: ['audit-logs', selectedBranchId],
    queryFn: () => rbacApi<{ items: SusuAuditLog[]; next: string | null }>(`/api/audit-logs?${queryParams.toString()}`),
    retry: 3,
    retryDelay: 1000
  });
}
export function useLoans() {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  return useQuery({
    queryKey: ['loans', selectedBranchId],
    queryFn: () => rbacApi<{ items: SusuLoan[]; next: string | null }>(`/api/loans?${queryParams.toString()}`),
    retry: 3,
    retryDelay: 1000
  });
}
export function useStaff(enabled = true) {
  return useQuery({
    queryKey: ['staff'],
    queryFn: () => rbacApi<{ items: User[]; next: string | null }>('/api/staff'),
    enabled,
  });
}
export function useCreateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<User>) => rbacApi<User>('/api/staff', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  });
}
export function useUpdateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) => rbacApi(`/api/staff/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  });
}
export function useBulkExport() {
  return async () => {
    try {
      const [clients, loans, txns, logs] = await Promise.all([
        rbacApi<{ items: any[] }>('/api/clients'),
        rbacApi<{ items: any[] }>('/api/loans'),
        rbacApi<{ items: any[] }>('/api/transactions'),
        rbacApi<{ items: any[] }>('/api/audit-logs')
      ]);
      const bundle = {
        clients: clients.items,
        loans: loans.items,
        transactions: txns.items,
        logs: logs.items,
        exportDate: new Date().toISOString()
      };
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `perfect-match-backup-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Institutional data bundle generated successfully");
    } catch (e) {
      toast.error("Failed to generate data backup");
    }
  };
}
export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => rbacApi('/api/transactions', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['client-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['client-self'] });
    },
  });
}
export function useCreateLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => rbacApi('/api/loans', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['loans'] }); },
  });
}
export function useUpdateLoanStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: any }) => rbacApi(`/api/loans/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}
export function exportToCSV(data: any[], filename: string) {
  if (!data || !data.length) return;
  const headers = Object.keys(data[0]).join(',');
  const rows = data.map(obj => Object.values(obj).map(val => {
    if (typeof val === 'string') return `"${val.replace(/"/g, '""')}"`;
    if (val === null || val === undefined) return '""';
    return val;
  }).join(',')).join('\n');
  const csv = `${headers}\n${rows}`;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function generatePDFPassbook(client: SusuClient, transactions?: SusuTransaction[]) { 
  if (!client) return;
  window.print(); 
}

export function useLoanSchedule(loanId: string) {
  return useQuery({
    queryKey: ['loan-schedule', loanId],
    queryFn: () => rbacApi<{ schedule: any[]; totalInterest: number; totalPayment: number }>(`/api/loans/${loanId}/schedule`),
    enabled: !!loanId,
  });
}

export function useOverdueLoans() {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  return useQuery({
    queryKey: ['overdue-loans', selectedBranchId],
    queryFn: () => rbacApi<{ items: SusuLoan[] }>(`/api/loans/overdue?${queryParams.toString()}`),
  });
}

export function usePostInterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, amount }: { loanId: string; amount?: number }) => 
      rbacApi(`/api/loans/${loanId}/interest`, { method: 'POST', body: JSON.stringify({ amount }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function usePostPenalty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, amount }: { loanId: string; amount?: number }) => 
      rbacApi(`/api/loans/${loanId}/penalty`, { method: 'POST', body: JSON.stringify({ amount }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useSendSMS() {
  return useMutation({
    mutationFn: ({ phone, message }: { phone: string; message: string }) => 
      rbacApi('/api/notifications/sms', { method: 'POST', body: JSON.stringify({ phone, message }) }),
  });
}

export function useBulkImportClients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clients: { name: string; phone: string; territory?: string; branchId?: string }[]) => 
      rbacApi('/api/clients/bulk-import', { method: 'POST', body: JSON.stringify({ clients }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useClientDocuments(clientId: string) {
  return useQuery({
    queryKey: ['client-documents', clientId],
    queryFn: () => rbacApi<{ items: any[] }>(`/api/clients/${clientId}/documents`),
    enabled: !!clientId,
  });
}

export function useUploadClientDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, type, url }: { clientId: string; type: 'ID_FRONT' | 'ID_BACK' | 'PROOF_OF_ADDRESS' | 'GUARANTOR_FORM'; url: string }) => 
      rbacApi(`/api/clients/${clientId}/documents`, { method: 'POST', body: JSON.stringify({ type, url }) }),
    onSuccess: (_, { clientId }) => queryClient.invalidateQueries({ queryKey: ['client-documents', clientId] }),
  });
}

export function useBranchPerformance(startDate?: string, endDate?: string) {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  return useQuery({
    queryKey: ['branch-performance', selectedBranchId, startDate, endDate],
    queryFn: () => rbacApi<{ items: { branch: string; amount: number }[] }>(`/api/reports/branch-performance?${queryParams.toString()}`),
  });
}

export function useOfficerLeaderboard(startDate?: string, endDate?: string) {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  return useQuery({
    queryKey: ['officer-leaderboard', selectedBranchId, startDate, endDate],
    queryFn: () => rbacApi<{ items: { name: string; collections: number }[] }>(`/api/reports/officer-leaderboard?${queryParams.toString()}`),
  });
}

export function useProfitLoss(startDate?: string, endDate?: string) {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  return useQuery({
    queryKey: ['profit-loss', selectedBranchId, startDate, endDate],
    queryFn: () => rbacApi<ProfitLossData>(`/api/reports/pnl?${queryParams.toString()}`),
  });
}

export function useCashFlow(startDate?: string, endDate?: string) {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  return useQuery({
    queryKey: ['cash-flow', selectedBranchId, startDate, endDate],
    queryFn: () => rbacApi<CashFlowData>(`/api/reports/cashflow?${queryParams.toString()}`),
  });
}

export function useAuditLogsDateRange(startDate?: string, endDate?: string) {
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const queryParams = new URLSearchParams();
  queryParams.append('branchId', selectedBranchId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  return useQuery({
    queryKey: ['audit-logs', selectedBranchId, startDate, endDate],
    queryFn: () => rbacApi<{ items: SusuAuditLog[]; next: string | null }>(`/api/audit-logs?${queryParams.toString()}`),
    retry: 3,
    retryDelay: 1000
  });
}

// ============================================
// NOTIFICATIONS
// ============================================

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => rbacApi<{ items: import('@shared/types').AppNotification[] }>('/api/notifications'),
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    retry: 1,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rbacApi(`/api/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => rbacApi('/api/notifications/read-all', { method: 'PATCH' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ============================================
// INTERNAL MESSAGING
// ============================================

export function useMessageThreads() {
  return useQuery({
    queryKey: ['message-threads'],
    queryFn: () => rbacApi<{ items: import('@shared/types').MessageThread[] }>('/api/messages/threads'),
    staleTime: 30000,
  });
}

export function useThreadMessages(threadId: string) {
  return useQuery({
    queryKey: ['thread-messages', threadId],
    queryFn: () => rbacApi<{ items: import('@shared/types').Message[] }>(`/api/messages/threads/${threadId}`),
    enabled: !!threadId,
    refetchInterval: 15000,
  });
}

export function useCreateThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { subject: string; participantIds: string[]; body: string }) =>
      rbacApi('/api/messages/threads', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['message-threads'] }),
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) =>
      rbacApi(`/api/messages/threads/${threadId}`, { method: 'POST', body: JSON.stringify({ body }) }),
    onSuccess: (_, { threadId }) => {
      queryClient.invalidateQueries({ queryKey: ['thread-messages', threadId] });
      queryClient.invalidateQueries({ queryKey: ['message-threads'] });
    },
  });
}
